/**
 * CWR 2.4 — fullscreen photo viewer (Gabriel-requested; NOT in the mockup).
 * Opens from a gallery thumbnail, animates up to fullscreen, letterboxes the
 * image (object-fit: contain → black bars).
 *
 * Gestures (direction-locked after the first few px):
 *  · horizontal swipe / arrow buttons / ←→ keys → previous / next photo
 *  · vertical drag DOWN → iOS-style dismiss: the image shrinks + the backdrop
 *    goes translucent (revealing the page) as you drag; past a threshold it
 *    dismisses, otherwise it springs back.
 * Closing: swipe-down or Esc here; the Mini App adds the Telegram BackButton
 * (DetailLightbox) and the gallery catches a history back. No in-app ✕ button.
 *
 * The display half, with no Telegram: the studio website draws it as is
 * (web-client-app phone service page).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { GalleryImage } from '@/api/types';
import { ChevronLeftIcon, ChevronRightIcon } from '../detailIcons';
import styles from '../SessionDetailPage.module.css';

export interface DetailLightboxProps {
  images: GalleryImage[];
  index: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

const AXIS_LOCK = 8; // px of travel before we commit to a horizontal/vertical gesture
const SWIPE_THRESHOLD = 48; // px horizontal travel that commits a photo change
const DISMISS_THRESHOLD = 110; // px downward travel that commits a dismiss
const DISMISS_RANGE = 320; // px mapped to full shrink/fade while dragging

export function DetailLightboxView({ images, index, onClose, onNavigate }: DetailLightboxProps) {
  const count = images.length;
  const start = useRef<{ x: number; y: number } | null>(null);
  const axis = useRef<'x' | 'y' | null>(null);
  const [dragX, setDragX] = useState(0); // live horizontal delta (photo nav)
  const [dragY, setDragY] = useState(0); // live downward delta (dismiss)
  const [dragging, setDragging] = useState(false);

  const goPrev = useCallback(() => {
    if (index > 0) onNavigate(index - 1);
  }, [index, onNavigate]);
  const goNext = useCallback(() => {
    if (index < count - 1) onNavigate(index + 1);
  }, [index, count, onNavigate]);

  // Keyboard: Esc closes, ←/→ navigate (desktop + a11y; harmless in Telegram).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, goPrev, goNext]);

  const reset = () => {
    start.current = null;
    axis.current = null;
    setDragX(0);
    setDragY(0);
    setDragging(false);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    start.current = { x: t?.clientX ?? 0, y: t?.clientY ?? 0 };
    axis.current = null;
    setDragging(true);
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!start.current) return;
    const t = e.touches[0];
    const dx = (t?.clientX ?? start.current.x) - start.current.x;
    const dy = (t?.clientY ?? start.current.y) - start.current.y;
    if (axis.current === null && Math.abs(dx) + Math.abs(dy) > AXIS_LOCK) {
      axis.current = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (axis.current === 'x') setDragX(dx);
    else if (axis.current === 'y') setDragY(Math.max(0, dy)); // dismiss is downward-only
  };
  const onTouchEnd = () => {
    const a = axis.current;
    const dx = dragX;
    const dy = dragY;
    if (a === 'y' && dy >= DISMISS_THRESHOLD) {
      onClose();
      return;
    }
    if (a === 'x') {
      if (dx <= -SWIPE_THRESHOLD) goNext();
      else if (dx >= SWIPE_THRESHOLD) goPrev();
    }
    reset(); // springs back (transition re-enabled) for a cancelled dismiss / short swipe
  };

  // Drag-down visuals: shrink the image + fade the backdrop/chrome toward the page.
  const progress = Math.min(dragY, DISMISS_RANGE) / DISMISS_RANGE;
  const scale = 1 - progress * 0.35;
  const backdropOpacity = 1 - progress * 0.85;
  const chromeOpacity = 1 - Math.min(progress * 1.4, 1);
  const settle = dragging ? 'none' : undefined; // CSS transition handles spring-back when not dragging

  // Portal to <body>: the gallery renders DEEP inside `.scroll`
  // (position:absolute; z-index:2 → its own stacking context), which would trap
  // this fixed overlay's z-index BELOW the detail back button (z-80) + bottom bar
  // (z-70) — both siblings of `.scroll` in `.page`. That trap is what let the
  // back button render ON TOP of the viewer and navigate away. The portal lifts
  // the overlay out of every app stacking context so it truly covers the page.
  return createPortal(
    <div className={styles.lightbox} role="dialog" aria-modal="true" aria-label="Photo viewer" data-testid="detail-lightbox">
      <div className={styles.lbBackdrop} style={{ opacity: backdropOpacity, transition: settle }} />

      {/* No in-app close button: the Telegram BackButton (owned while open),
          swipe-down-to-dismiss, and Esc all close the viewer. */}
      <div
        className={styles.lbViewport}
        style={{ transform: `translate3d(0, ${dragY}px, 0) scale(${scale})`, transition: settle }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        data-testid="lightbox-viewport"
      >
        <div
          className={styles.lbTrack}
          style={{ transform: `translate3d(calc(${-index * 100}% + ${dragX}px), 0, 0)`, transition: settle }}
          data-testid="lightbox-track"
        >
          {images.map((img, i) => (
            <div className={styles.lbSlide} key={`${img.url}-${i}`}>
              {/* alt empty: decorative gallery photo (the page already names the offering) */}
              <img className={styles.lbImg} src={img.url} alt="" draggable={false} />
            </div>
          ))}
        </div>
      </div>

      {index > 0 && (
        <button
          type="button"
          className={`${styles.lbArrow} ${styles.lbPrev}`}
          onClick={goPrev}
          aria-label="Previous photo"
          data-testid="lightbox-prev"
          style={{ opacity: chromeOpacity, transition: settle }}
        >
          {ChevronLeftIcon}
        </button>
      )}
      {index < count - 1 && (
        <button
          type="button"
          className={`${styles.lbArrow} ${styles.lbNext}`}
          onClick={goNext}
          aria-label="Next photo"
          data-testid="lightbox-next"
          style={{ opacity: chromeOpacity, transition: settle }}
        >
          {ChevronRightIcon}
        </button>
      )}

      {count > 1 && (
        <div
          className={styles.lbCounter}
          data-testid="lightbox-counter"
          style={{ opacity: chromeOpacity, transition: settle }}
        >
          {index + 1} / {count}
        </div>
      )}
    </div>,
    document.body,
  );
}
